
import { onMounted, reactive, ref } from "vue";
import dayjs from "dayjs";
import XAtable from "@/components/XATable.vue";
import XDialog from "@/components/XDialog.vue";
import { getWalletList, walletCheck, exportWalletXls } from "@/api/api";
import { FormInstance, message } from "ant-design-vue";

/** 财务密码：6 位纯数字 */
const FINANCIAL_PIN_PATTERN = /^\d{6}$/;

interface SearchFormState {
  userId: string;
  userName: string;
  type: number | string | undefined;
  verify: number | string | undefined;
  timeRange: [string, string] | null | undefined;
  pageNo: number;
  pageSize: number;
}

interface CheckFormState {
  id: number | undefined;
  number: string;
  password: string;
  verify: number;
}

export default {
  name: "WalletList",
  components: {
    XAtable,
    XDialog,
  },
  setup() {
    const searchFormState = reactive<SearchFormState>({
      userId: "",
      userName: "",
      type: undefined,
      verify: undefined,
      timeRange: null,
      pageNo: 1,
      pageSize: 10,
    });

    const dateFormat = ref("YYYY-MM-DD");

    const pageNo = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    const tableData = ref([]);
    const searchFormRef = ref<FormInstance>();
    const checkDialogVisible = ref(false);
    const checkLoading = ref(false);
    const exportLoading = ref(false);

    const exportXls = () => {
      exportLoading.value = true;
      const params: any = {};
      if (searchFormState.userId) params.userId = searchFormState.userId;
      if (searchFormState.userName) params.userName = searchFormState.userName;
      if (searchFormState.type !== undefined && searchFormState.type !== "") params.type = searchFormState.type;
      if (searchFormState.verify !== undefined && searchFormState.verify !== "") params.verify = searchFormState.verify;
      if (searchFormState.timeRange?.length === 2) {
        const [startDay, endDay] = searchFormState.timeRange;
        params.beginTime = `${startDay} 00:00:00`;
        const endPlusOne = dayjs(endDay, dateFormat.value).add(1, "day").format(dateFormat.value);
        params.endTime = `${endPlusOne} 23:59:59`;
      }
      exportWalletXls(params)
        .then((res: unknown) => {
          exportLoading.value = false;
          if (res instanceof Blob) {
            const url = window.URL.createObjectURL(res);
            const link = document.createElement("a");
            link.href = url;
            link.download = `钱包明细_${dayjs().format("YYYY-MM-DD_HHmm")}.xls`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            message.success("导出成功！");
          } else {
            message.error("导出失败，请重试！");
          }
        })
        .catch((error: any) => {
          exportLoading.value = false;
          message.error("导出失败，请重试！");
        });
    };
    const checkFormState = reactive<CheckFormState>({
      id: undefined,
      number: "",
      password: "",
      verify: 1,
    });

    const columns = reactive([
      {
        title: "ID",
        key: "id",
        dataIndex: "id",
        width: 80,
      },
      {
        title: "用户",
        key: "userInfo",
        dataIndex: "userInfo",
        width: 150,
        slots: {
          customRender: "userInfo",
        },
      },
      {
        title: "金额",
        key: "wallet",
        dataIndex: "wallet",
        width: 120,
        slots: {
          customRender: "wallet",
        },
      },
      {
        title: "类型",
        key: "type",
        dataIndex: "type",
        width: 120,
        slots: {
          customRender: "type",
        },
      },
      {
        title: "说明",
        key: "remark",
        dataIndex: "remark",
        width: 200,
      },
      {
        title: "备注",
        key: "remark2",
        dataIndex: "remark2",
        width: 100,
        slots: {
          customRender: "remark2",
        },
      },
      {
        title: "创建时间",
        key: "createTime",
        dataIndex: "createTime",
        width: 180,
      },
      {
        title: "财务核验",
        key: "verify",
        dataIndex: "verify",
        width: 140,
        slots: {
          customRender: "verify",
        },
      },
      {
        title: "操作",
        key: "action",
        dataIndex: "action",
        width: 120,
        slots: {
          customRender: "action",
        },
      },
    ]);

    /** 仅「钱包」类型且金额为正（充值入账）显示审核 */
    const showWalletAudit = (record: any) => {
      return record.type === 0 && Number(record.wallet) > 0;
    };

    /** 已核验时在 verify 下方展示「编号：xxx」 */
    const walletVerifyNumberLine = (n: unknown): string | null => {
      if (n === undefined || n === null || n === "") return null;
      const s = String(n).trim();
      if (!s) return null;
      return `编号：${s}`;
    };

    const openCheckDialog = (record: any) => {
      console.log(checkFormState.number);
      if (!showWalletAudit(record)) return;
      checkFormState.id = record.id;
      const n = record.number;
      checkFormState.number = n || "";
      checkFormState.password = "";
      checkFormState.verify =
        record.verify === 1 || record.verify === 2 ? record.verify : 1;
      checkDialogVisible.value = true;
    };

    const confirmCheck = () => {
      if (!checkFormState.id) {
        message.error("缺少列表ID");
        return;
      }
      if (checkFormState.verify === 1) {
        if (!checkFormState.number) {
          message.error("请填写编号");
          return;
        }
      }
      if (!checkFormState.password) {
        message.error("请输入密码");
        return;
      }
      if (!FINANCIAL_PIN_PATTERN.test(checkFormState.password)) {
        message.error("财务密码须为6位纯数字");
        return;
      }
      if (![1, 2].includes(checkFormState.verify)) {
        message.error("请选择财务核验状态");
        return;
      }
      checkLoading.value = true;
      const checkPayload: Record<string, unknown> = {
        id: checkFormState.id,
        password: checkFormState.password,
        verify: checkFormState.verify,
      };
      if (checkFormState.verify === 1) {
        checkPayload.number = checkFormState.number;
      }
      walletCheck(checkPayload)
        .then((res) => {
          if (res.code === "200") {
            message.success("审核成功");
            checkDialogVisible.value = false;
            getData();
          }
        })
        .finally(() => {
          checkLoading.value = false;
        });
    };

    const search = () => {
      searchFormState.pageNo = 1;
      pageNo.value = 1;
      getData();
    };

    const resetForm = () => {
      searchFormState.pageNo = 1;
      pageNo.value = 1;
      searchFormRef.value?.resetFields();
      searchFormState.timeRange = null;
      getData();
    };

    const getData = () => {
      loading.value = true;
      const params: any = {
        pageNo: searchFormState.pageNo,
        pageSize: searchFormState.pageSize,
      };

      if (searchFormState.userId) {
        params.userId = searchFormState.userId;
      }
      if (searchFormState.userName) {
        params.userName = searchFormState.userName;
      }
      if (searchFormState.type !== undefined && searchFormState.type !== "") {
        params.type = searchFormState.type;
      }
      if (searchFormState.verify !== undefined && searchFormState.verify !== "") {
        params.verify = searchFormState.verify;
      }
      if (
        searchFormState.timeRange &&
        Array.isArray(searchFormState.timeRange) &&
        searchFormState.timeRange.length === 2
      ) {
        const [startDay, endDay] = searchFormState.timeRange;
        params.beginTime = `${startDay} 00:00:00`;
        const endPlusOne = dayjs(endDay, dateFormat.value).add(1, "day").format(dateFormat.value);
        params.endTime = `${endPlusOne} 23:59:59`;
      }

      getWalletList(params).then((res) => {
        loading.value = false;
        if (res.code === "200") {
          total.value = res.data.total || 0;
          tableData.value = res.data.records || [];
        }
      });
    };

    const pageNoChange = (val: number) => {
      searchFormState.pageNo = val;
      pageNo.value = val;
      getData();
    };

    const onShowSizeChange = (current: number, size: number) => {
      searchFormState.pageSize = size;
      pageSize.value = size;
      searchFormState.pageNo = 1;
      pageNo.value = 1;
      getData();
    };

    onMounted(() => {
      getData();
    });

    return {
      dateFormat,
      searchFormState,
      searchFormRef,
      columns,
      tableData,
      pageNo,
      pageSize,
      total,
      loading,
      checkDialogVisible,
      checkLoading,
      checkFormState,
      search,
      resetForm,
      pageNoChange,
      onShowSizeChange,
      openCheckDialog,
      confirmCheck,
      showWalletAudit,
      walletVerifyNumberLine,
      exportLoading,
      exportXls,
    };
  },
};
